// The season wrapped (scenario H9, PLAN-2026-10-18-families-and-newcomers
// §12.1): a team's season told as a few cards from the season's own facts,
// and the words for one image to share. Pure: the awards engine's facts and
// the minted awards in, cards out. No clocks, no I/O, no sport names.
//
// THIRD PERSON, BY TEAM NAME: the image is made to be passed around, and a
// line that said "you" would be wrong the moment it left the phone.

import type { IconId, Minted, SeasonFacts, SecondGameFact, Tone } from '@fsp/awards';
import { ordinal, possessive, pts, recordOf, sideOf, times, winnerOf } from './season-words';

export type WrapHow = 'champion' | 'runner-up' | 'playoffs' | 'missed' | 'finished';
export type WrapCardId =
  'finish' | 'best-week' | 'awards' | 'pickup' | 'rivalry' | 'pickem' | 'league';

export interface WrapAward {
  awardId: string;
  name: string;
  icon: IconId;
  tone: Tone;
  period: number | null;
  line: string;
}

export interface WrapCard {
  id: WrapCardId;
  title: string;
  /** The big figure: a record, a score, a count. */
  value: string | null;
  line: string;
  /** Another team the card names: the rival, the champion, the best week's opponent. */
  teamId: string | null;
  matchupId: string | null;
  awards: WrapAward[];
}

export interface WrapShare {
  kicker: string;
  headline: string;
  subline: string;
  line: string;
  tone: Tone;
}

export interface Wrap {
  over: boolean;
  how: WrapHow | null;
  cards: WrapCard[];
  share: WrapShare | null;
}

export interface WrapInput {
  facts: SeasonFacts;
  teamId: string;
  /** This season's awards as handed out; a superseded one is left out here. */
  mints: readonly Minted[];
  /** "Week 7", "Semifinal": the league's own name for a week. */
  periodLabel: (period: number) => string;
}

export function seasonWrap(input: WrapInput): Wrap {
  const { facts, teamId, mints, periodLabel } = input;
  const nameOf = (id: string | null) =>
    (id === null ? null : facts.teams.find((t) => t.teamId === id)?.name) ?? 'a team';
  const team = nameOf(teamId);
  const finals = facts.periods.filter((p) => p.final);
  const games = finals.flatMap((p) => p.matchups.filter((m) => m.final));
  const mine = games.filter((m) => m.homeTeamId === teamId || m.awayTeamId === teamId);
  const b = facts.bracket;

  // ── Over, and how ───────────────────────────────────────────────────────
  // A LEG OF A TWO-WEEK FINAL LOST IS NOT THE END: the title goes on the
  // total, and only the recorded champion says who won it.
  const finalWeeks = new Set(b.finalPeriods ?? []);
  const lostPlayoff = mine
    .filter((m) => m.kind === 'playoff' && !finalWeeks.has(m.period))
    .find((m) => {
      const w = winnerOf(m);
      return w !== null && w !== teamId;
    });
  // THE FIELD IS A FIELD ONCE THE REGULAR SEASON IS DONE; before that it is
  // a projection (the simulator's "if it ended today"), which leaves nobody
  // out yet.
  const fieldSet = facts.periods.some((p) => p.period === facts.regularSeasonEnd && p.final);
  let how: WrapHow | null = null;
  if (b.final) {
    how =
      b.championTeamId === teamId
        ? 'champion'
        : b.playoffTeamIds.length === 0
          ? // A LEAGUE WITH NO BRACKET (B-362) had no final to lose.
            'finished'
          : b.runnerUpTeamId === teamId
            ? 'runner-up'
            : b.playoffTeamIds.includes(teamId)
              ? 'playoffs'
              : 'missed';
  } else if (b.playoffTeamIds.length > 0 && fieldSet) {
    if (!b.playoffTeamIds.includes(teamId)) how = 'missed';
    else if (lostPlayoff) how = 'playoffs';
  }
  if (how === null) return { over: false, how: null, cards: [], share: null };

  const cards: WrapCard[] = [];
  const card = (c: Omit<WrapCard, 'teamId' | 'matchupId' | 'awards'> & Partial<WrapCard>) =>
    cards.push({ teamId: null, matchupId: null, awards: [], ...c });

  // ── The finish ─────────────────────────────────────────────────────────
  const standing = facts.standings.find((s) => s.teamId === teamId);
  const of = facts.standings.length;
  const played = standing ? standing.wins + standing.losses + standing.ties : 0;
  const record = standing
    ? played > 0
      ? recordOf(standing.wins, standing.losses, standing.ties)
      : `${pts(standing.pointsFor)} points`
    : null;
  const place = standing
    ? `${ordinal(standing.rank)} of ${of} in the regular season, ${pts(standing.pointsFor)} points`
    : 'The regular season done';
  let ending: string;
  // THE TROPHY'S NAME IS A TITLE, "League Champion", worn rather than won,
  // as the league's own champion line says it ("are your 2026 League
  // Champion").
  if (how === 'champion') {
    ending = `and the ${facts.season} ${facts.trophyName}. ${b.finalLine ?? ''}`.trim();
  } else if (how === 'runner-up') {
    ending = `then the final, which ${nameOf(b.championTeamId)} won.`;
  } else if (how === 'playoffs') {
    const out = [...mine]
      .filter((m) => m.kind === 'playoff')
      .reverse()
      .find((m) => {
        const w = winnerOf(m);
        return w !== null && w !== teamId;
      });
    if (out) {
      const s = sideOf(out, teamId);
      // A ROUND IS "the Semifinal", A WEEK IS "Week 16": a league whose
      // playoff weeks carry no round's name read "out in the Week 16".
      const label = periodLabel(out.period);
      const at = /^Week /.test(label) ? label : `the ${label}`;
      ending = `then out in ${at} to ${nameOf(s.opponent)}, ${pts(s.score)} to ${pts(s.against)}.`;
    } else ending = 'then the playoffs.';
  } else if (how === 'missed') {
    ending = 'and no playoffs this time.';
  } else ending = '';
  card({
    id: 'finish',
    title: 'The finish',
    value: record,
    line: ending ? `${place}, ${ending}` : `${place}.`,
  });

  // ── The best week ──────────────────────────────────────────────────────
  let best: {
    period: number;
    score: number;
    opponent: string | null;
    result: string | null;
    /** The week's second game, in a league of two opponents a week (Q6). */
    second: SecondGameFact | null;
  } | null = null;
  let leagueBest: { period: number; score: number; teamId: string } | null = null;
  for (const p of finals)
    for (const t of p.teams) {
      if (!t.played) continue;
      if (leagueBest === null || t.score > leagueBest.score)
        leagueBest = { period: p.period, score: t.score, teamId: t.teamId };
      if (t.teamId === teamId && (best === null || t.score > best.score))
        best = {
          period: p.period,
          score: t.score,
          opponent: t.opponentTeamId,
          result: t.result,
          second: t.second ?? null,
        };
    }
  if (best !== null && best.score > 0) {
    const game = mine.find(
      (m) =>
        m.period === best.period &&
        (best.opponent === null || sideOf(m, teamId).opponent === best.opponent),
    );
    const against = game ? sideOf(game, teamId).against : null;
    const results = [best.result, best.second?.result ?? null];
    const won = results.filter((r) => r === 'win').length;
    const lost = results.filter((r) => r === 'loss').length;
    const told =
      best.second !== null && best.opponent !== null
        ? // A WEEK OF TWO GAMES (Q6): both opponents, and how the two went.
          `${periodLabel(best.period)}, against ${nameOf(best.opponent)} and ${nameOf(best.second.opponentTeamId)}: ${pts(best.score)}, ${
            won === 2
              ? 'two wins'
              : lost === 2
                ? 'and still two losses'
                : won === 1 && lost === 1
                  ? 'a win and a loss'
                  : won === 1
                    ? 'a win and a tie'
                    : lost === 1
                      ? 'a loss and a tie'
                      : 'two ties'
          }.`
        : best.opponent === null || against === null
          ? `${periodLabel(best.period)}: ${pts(best.score)}.`
          : `${periodLabel(best.period)}, against ${nameOf(best.opponent)}: ${pts(best.score)} to ${pts(against)}, ${
              best.result === 'win'
                ? 'a win'
                : best.result === 'loss'
                  ? 'and still a loss'
                  : 'a tie'
            }.`;
    const top =
      leagueBest !== null && leagueBest.teamId === teamId && leagueBest.period === best.period
        ? ' The league’s highest week of the season.'
        : '';
    card({
      id: 'best-week',
      title: 'The best week',
      value: pts(best.score),
      line: `${told}${top}`,
      teamId: best.opponent,
      matchupId: game?.id ?? null,
    });
  }

  // ── The awards ─────────────────────────────────────────────────────────
  const won = mints.filter((m) => !m.supersededBy && m.winners.some((w) => w.teamId === teamId));
  if (won.length > 0) {
    const weekly = won.filter((m) => m.scope === 'period').length;
    const season = won.filter((m) => m.scope === 'season').map((m) => m.name);
    const parts = [
      weekly > 0 ? `${weekly} weekly award${weekly === 1 ? '' : 's'}` : null,
      season.length > 0 ? season.join(', ') : null,
    ].filter((x): x is string => x !== null);
    card({
      id: 'awards',
      title: 'The awards',
      value: String(won.length),
      line: `${team} took ${parts.join(' and ')}.`,
      awards: [...won]
        .sort((a, c) => (a.period ?? 999) - (c.period ?? 999))
        .map((m) => ({
          awardId: m.awardId,
          name: m.name,
          icon: m.icon,
          tone: m.tone,
          period: m.period,
          line: m.line,
        })),
    });
  }

  // ── The pickup ─────────────────────────────────────────────────────────
  const pickups = facts.periods
    .flatMap((p) => p.transactions)
    .filter((x) => x.teamId === teamId && (x.kind === 'add' || x.kind === 'claim'));
  const pickup = pickups.reduce<(typeof pickups)[number] | null>(
    (top, x) => (top === null || x.pointsSince > top.pointsSince ? x : top),
    null,
  );
  if (pickup !== null && pickup.pointsSince > 0) {
    card({
      id: 'pickup',
      title: 'The pickup',
      value: pts(pickup.pointsSince),
      line: `${facts.playerName(pickup.playerId)} came ${
        pickup.kind === 'claim' ? 'off waivers' : 'in as a free agent'
      } in ${periodLabel(pickup.period)} and scored ${pts(pickup.pointsSince)} for ${team}.`,
    });
  }

  // ── The rivalry ────────────────────────────────────────────────────────
  const series = new Map<
    string,
    { met: number; w: number; l: number; t: number; margin: number }
  >();
  for (const m of mine) {
    const s = sideOf(m, teamId);
    const row = series.get(s.opponent) ?? { met: 0, w: 0, l: 0, t: 0, margin: 0 };
    const w = winnerOf(m);
    row.met += 1;
    if (w === teamId) row.w += 1;
    else if (w === null) row.t += 1;
    else row.l += 1;
    row.margin += Math.abs(s.score - s.against);
    series.set(s.opponent, row);
  }
  const rival = [...series.entries()]
    .filter(([, r]) => r.met >= 2)
    .sort(([, a], [, c]) => c.met - a.met || a.margin - c.margin)[0];
  if (rival) {
    const [opponent, r] = rival;
    card({
      id: 'rivalry',
      title: 'The rivalry',
      value: recordOf(r.w, r.l, r.t),
      line: `${team} met ${nameOf(opponent)} ${times(r.met)}: ${recordOf(r.w, r.l, r.t)}, with ${pts(r.margin)} points between them in all.`,
      teamId: opponent,
    });
  }

  // ── Pick'em ────────────────────────────────────────────────────────────
  const picks = facts.pickem ?? {};
  if (picks[teamId] !== undefined) {
    const table = Object.entries(picks).sort(([, a], [, c]) => c - a);
    const points = picks[teamId]!;
    const rank = 1 + table.filter(([, p]) => p > points).length;
    // A point a winner, half a tie: 23 or 23.5, never 23.0.
    const said = Number.isInteger(points) ? String(points) : points.toFixed(1);
    card({
      id: 'pickem',
      title: "Pick'em",
      value: said,
      line: `${ordinal(rank)} of ${table.length} at pick'em, with ${said} point${points === 1 ? '' : 's'}.`,
    });
  }

  // ── The league ─────────────────────────────────────────────────────────
  const decided = b.final && b.championTeamId !== null;
  const highest =
    leagueBest !== null
      ? ` The season’s highest week: ${pts(leagueBest.score)}, ${nameOf(leagueBest.teamId)}, ${periodLabel(leagueBest.period)}.`
      : '';
  card({
    id: 'league',
    title: 'The league',
    value: decided ? nameOf(b.championTeamId) : null,
    line: decided
      ? `${nameOf(b.championTeamId)} are the ${facts.season} ${facts.trophyName}. ${b.finalLine ?? ''}`.trim() +
        highest
      : `The ${facts.season} ${facts.trophyName} is still to be decided.${highest}`,
    teamId: decided ? b.championTeamId : null,
  });

  // ── The image's words ─────────────────────────────────────────────────
  const finish =
    how === 'champion'
      ? 'Champions'
      : how === 'runner-up'
        ? 'Runners-up'
        : standing
          ? `${ordinal(standing.rank)} of ${of}`
          : '';
  const bits = [
    best !== null && best.score > 0 ? `Best week ${pts(best.score)}.` : null,
    won.length > 0 ? `${won.length} award${won.length === 1 ? '' : 's'}.` : null,
    pickup !== null && pickup.pointsSince > 0
      ? `Top pickup ${facts.playerName(pickup.playerId)}, ${pts(pickup.pointsSince)}.`
      : null,
  ].filter((x): x is string => x !== null);
  return {
    over: true,
    how,
    cards,
    share: {
      kicker: `${facts.season} season`,
      headline: team,
      subline: [record, finish].filter(Boolean).join(' · '),
      line:
        bits.length > 0
          ? bits.join(' ')
          : `${possessive(team)} ${facts.season} season, in the books.`,
      tone: how === 'champion' || how === 'runner-up' ? 'glory' : 'fun',
    },
  };
}
